import { useMemo } from 'react';
import { ReactComponent as NotificationsMuteIcon } from '@elementary/common-ui/assets/20x20/notifications-mute.svg';
import { ReactComponent as NotificationsIcon } from '@elementary/common-ui/assets/20x20/notifications.svg';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import { ReactComponent as DestinationIcon } from '@elementary/common-ui/assets/20x20/destination.svg';
import {
  ActionsMenuButton,
  FloatingActionsMenu,
  Menu,
} from '@elementary/common-ui/components';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { TestManagementTabs } from 'testManagement/types';

type Props = {
  selectedCount: number;
  mode: TestManagementTabs;
  onChange: (action: string) => void;
  onClose: () => void;
  deleteDisabledTooltip?: string;
  editDestinationsDisabledTooltip?: string;
};

const TestManagementActionsMenu = ({
  selectedCount,
  mode,
  onChange,
  onClose,
  deleteDisabledTooltip,
  editDestinationsDisabledTooltip,
}: Props) => {
  const hasPermission = useEnvPermission();

  const disableEditTestActionsTooltip = !hasPermission('EDIT_ALERT_RULES')
    ? 'You do not have permission to modify alerts'
    : undefined;

  const actionsMenuOptions = useMemo(() => {
    const sharedActions = [
      {
        label: 'Delete',
        value: 'delete',
        icon: <TrashIcon />,
        disabled: !!deleteDisabledTooltip,
        tooltip: deleteDisabledTooltip || undefined,
      },
    ];

    if (mode === 'alerts') {
      return [
        ...sharedActions,
        {
          label: 'Alert Destinations',
          value: 'alertDestinations',
          icon: <DestinationIcon />,
          disabled:
            !!disableEditTestActionsTooltip ||
            !!editDestinationsDisabledTooltip,
          tooltip:
            disableEditTestActionsTooltip || editDestinationsDisabledTooltip,
        },
        {
          value: 'notification',
          itemRenderer: () => (
            <Menu
              menuButton={
                <ActionsMenuButton
                  tooltip={editDestinationsDisabledTooltip}
                  disabled={!!editDestinationsDisabledTooltip}
                  label="Alerts"
                  icon={<NotificationsIcon />}
                />
              }
              options={[
                {
                  value: 'mute',
                  label: 'Disable',
                  icon: <NotificationsMuteIcon />,
                },
                {
                  value: 'unmute',
                  label: 'Enable',
                  icon: <NotificationsIcon />,
                },
              ]}
              onChange={(val) => onChange(val)}
              closeOnChange
              anchorOrigin={{ vertical: -110, horizontal: 0 }}
            />
          ),
        },
      ];
    }

    return sharedActions;
  }, [
    deleteDisabledTooltip,
    disableEditTestActionsTooltip,
    editDestinationsDisabledTooltip,
    mode,
    onChange,
  ]);

  return (
    <FloatingActionsMenu
      itemCount={selectedCount}
      countLabel="Test"
      countLabelPlural="Tests"
      actions={actionsMenuOptions}
      onChange={onChange}
      onClose={onClose}
    />
  );
};

export default TestManagementActionsMenu;
